import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { AiOutlineClose } from 'react-icons/ai';
import {
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
  FiDownload,
  FiExternalLink,
  FiHelpCircle,
  FiMail,
  FiMapPin,
  FiPhone,
  FiSearch,
} from 'react-icons/fi';

import { checkMot } from '../../ressources/functionJS/checkMot';
import * as url from '../../ressources/glossaires/glossaire_liens_pages';
import argosLogo from '../../ressources/Logo/nlogo.png';
import styles from './Formation.module.scss';
import { FAQ_CATEGORIES } from './faqData';
import { renderFaqAnswer, stripFaqTokens } from './faqIcons';
import { FORMATION_GUIDES, FORMATION_VIDEOS } from './formationMedia';

const Formation = () => {
  const history = useHistory();
  const [search, setSearch] = useState('');
  const [openKey, setOpenKey] = useState(
    'Comment changer la langue de la plateforme ?'
  );
  const [modalOpen, setModalOpen] = useState(false);
  const columnsRef = useRef(null);

  // Fermer le modal avec la touche Échap et bloquer le scroll du fond.
  useEffect(() => {
    if (!modalOpen) {
      return undefined;
    }
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setModalOpen(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [modalOpen]);

  const scrollColumns = (direction) => {
    if (columnsRef.current) {
      columnsRef.current.scrollBy({
        left: direction * 380,
        behavior: 'smooth',
      });
    }
  };

  const openInNewWindow = (path, width = 1200, height = 750) => {
    window.open(
      path,
      '_blank',
      `toolbar=0,location=0,menubar=0,width=${width},height=${height}`
    );
  };

  const handleClose = () => {
    history.push(url.accueil);
  };

  const toggleItem = (key) => {
    setOpenKey((current) => (current === key ? '' : key));
  };

  const query = search.trim().toLowerCase();

  const filteredCategories = useMemo(() => {
    if (!query) {
      return FAQ_CATEGORIES;
    }
    return FAQ_CATEGORIES.map((category) => ({
      ...category,
      items: category.items.filter(({ question, answer }) =>
        (question + ' ' + stripFaqTokens(answer)).toLowerCase().includes(query)
      ),
    })).filter((category) => category.items.length > 0);
  }, [query]);

  const renderItem = (item) => {
    const isOpen = openKey === item.question;
    return (
      <li key={item.question} className={styles.faqItem}>
        <button
          type="button"
          className={styles.faqQuestion}
          onClick={() => toggleItem(item.question)}
          aria-expanded={isOpen}
        >
          {isOpen ? (
            <FiChevronDown className={styles.faqChevron} size={18} />
          ) : (
            <FiChevronRight className={styles.faqChevron} size={18} />
          )}
          <span>{checkMot(item.question)}</span>
        </button>
        {isOpen && (
          <p className={styles.faqAnswer}>
            {renderFaqAnswer(checkMot(item.answer))}
          </p>
        )}
      </li>
    );
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerTitle}>
          <FiHelpCircle className={styles.headerIcon} size={40} />
          {checkMot('Support & Aide')}
        </div>
        <button
          type="button"
          className={styles.closeBtn}
          onClick={handleClose}
          aria-label={checkMot('fermer')}
        >
          <AiOutlineClose size={68} />
        </button>
      </header>

      <div className={styles.body}>
        <aside className={styles.leftCol}>
          <div className={styles.guideBox}>
            <div className={styles.guideTitle}>
              {checkMot('Téléchargez le guide complet d’ARGOS')}
            </div>
            <div className={styles.guideButtons}>
              {FORMATION_GUIDES.map((guide) => (
                <button
                  key={guide.key}
                  type="button"
                  className={styles.downloadBtn}
                  onClick={() => guide.pdf && openInNewWindow(guide.pdf)}
                  disabled={!guide.pdf}
                >
                  <span>{checkMot(guide.label)}</span>
                  <FiDownload size={18} />
                </button>
              ))}
            </div>
          </div>

          <div className={styles.faqBox}>
            <button
              type="button"
              className={styles.faqExpand}
              onClick={() => setModalOpen(true)}
              aria-label={checkMot('Agrandir la FAQ')}
              title={checkMot('Agrandir la FAQ')}
            >
              <FiExternalLink size={20} />
            </button>
            <h2 className={styles.faqTitle}>{checkMot('FAQ')}</h2>

            <div className={styles.searchWrap}>
              <FiSearch className={styles.searchIcon} size={18} />
              <input
                type="text"
                className={styles.searchInput}
                placeholder={checkMot('Cherchez ce que vous voulez ...')}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>

            <div className={styles.faqScroll}>
              {filteredCategories.length === 0 && (
                <p className={styles.faqEmpty}>
                  {checkMot('Aucun résultat trouvé.')}
                </p>
              )}
              {filteredCategories.map((category) => (
                <div key={category.title} className={styles.faqCategory}>
                  <h3 className={styles.faqSection}>
                    {checkMot(category.title)}
                  </h3>
                  <ul className={styles.faqList}>
                    {category.items.map(renderItem)}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </aside>

        <section className={styles.rightCol}>
          <div className={styles.videoGrid}>
            {FORMATION_VIDEOS.map((card) => (
              <button
                key={card.key}
                type="button"
                className={styles.videoCard}
                onClick={() => card.video && openInNewWindow(card.video)}
                disabled={!card.video}
              >
                <span className={styles.videoCardTitle}>
                  {checkMot(card.title)}
                </span>
                <span className={styles.videoThumb}>
                  {card.video ? (
                    <video
                      className={styles.videoPreview}
                      src={card.video}
                      poster={card.poster}
                      preload="metadata"
                      muted
                    />
                  ) : (
                    <img
                      className={styles.videoPreview}
                      src={card.poster}
                      alt={checkMot(card.title)}
                    />
                  )}
                  <span className={styles.videoOverlay} aria-hidden="true" />
                </span>
              </button>
            ))}
          </div>
        </section>
      </div>

      <footer className={styles.footer}>
        <div className={styles.contactList}>
          <a className={styles.contactItem} href="tel:+33141064898">
            <FiPhone size={18} className={styles.contactIcon} />
            +33 (0)141064898
          </a>
          <a
            className={styles.contactItem}
            href="mailto:contact@spiebatignolles-itm.fr"
          >
            <FiMail size={18} className={styles.contactIcon} />
            contact@spiebatignolles-itm.fr
          </a>
          <span className={styles.contactItem}>
            <FiMapPin size={18} className={styles.contactIcon} />
            Arboretum, 1 Place des Papeteries, 92000 Nanterre
          </span>
        </div>
        <img className={styles.logo} src={argosLogo} alt="ARGOS" />
      </footer>

      {modalOpen && (
        <div className={styles.modalOverlay} role="dialog" aria-modal="true">
          <div className={styles.modalHeader}>
            <button
              type="button"
              className={styles.modalBack}
              onClick={() => setModalOpen(false)}
            >
              <FiChevronLeft size={22} />
              <span>{checkMot('Support & Aide')}</span>
            </button>
            <h2 className={styles.modalTitle}>{checkMot('FAQ')}</h2>
            <span aria-hidden="true" />
          </div>

          <div className={styles.modalSearchWrap}>
            <FiSearch className={styles.searchIcon} size={18} />
            <input
              type="text"
              className={styles.searchInput}
              placeholder={checkMot('Cherchez ce que vous voulez ...')}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>

          <div className={styles.modalScrollArea}>
            <button
              type="button"
              className={`${styles.modalArrow} ${styles.modalArrowLeft}`}
              onClick={() => scrollColumns(-1)}
              aria-label={checkMot('Afficher moins')}
            >
              <FiChevronLeft size={26} />
            </button>

            <div className={styles.modalColumns} ref={columnsRef}>
              {filteredCategories.length === 0 && (
                <p className={styles.faqEmpty}>
                  {checkMot('Aucun résultat trouvé.')}
                </p>
              )}
              {filteredCategories.map((category) => (
                <div key={category.title} className={styles.modalCategory}>
                  <h3 className={styles.modalSection}>
                    {checkMot(category.title).replace(/^\d+\.\s*/, '')}
                  </h3>
                  <div className={styles.modalCategoryScroll}>
                    <ul className={styles.faqList}>
                      {category.items.map(renderItem)}
                    </ul>
                  </div>
                </div>
              ))}
            </div>

            <button
              type="button"
              className={`${styles.modalArrow} ${styles.modalArrowRight}`}
              onClick={() => scrollColumns(1)}
              aria-label={checkMot('Afficher plus')}
            >
              <FiChevronRight size={26} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Formation;
